import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { LayoutGrid, Link2, Waves, Sparkles } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { getEffectiveLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { NuxxumLogo, NuxxumWordmark } from "@/components/NuxxumLogo";
import { LaunchPromoBanner } from "@/components/LaunchPromoBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const COLLAGE = [
  { label: "Familia", emoji: "🏡", img: "https://static.prod-images.emergentagent.com/jobs/05a06bde-6151-4ae1-aea4-c20178d025ef/images/7e6cd2260ac532e543fb86baa4315ce2c6cbc9c955ff57d1fe94e9d00d31d485.png" },
  { label: "Trabajo", emoji: "💼", img: "https://static.prod-images.emergentagent.com/jobs/05a06bde-6151-4ae1-aea4-c20178d025ef/images/4b0459d6d69ac7c3fe9be6c59da39d89a370362b4c009e7039f2356b20365c74.png" },
  { label: "Viajes", emoji: "✈️", img: "https://static.prod-images.emergentagent.com/jobs/05a06bde-6151-4ae1-aea4-c20178d025ef/images/55c8e2b3fed2c6281d54515d27744688946700d0b5461418490102d4067a836d.png" },
  { label: "Clubes", emoji: "⚽", img: "https://static.prod-images.emergentagent.com/jobs/05a06bde-6151-4ae1-aea4-c20178d025ef/images/b28b410537f04ecf9486d578599f14d308625784f654eb1e67532ecf544b1496.png" },
];

const VALUE_PROPS = [
  { icon: LayoutGrid, color: "text-[#9A94FF]", bg: "bg-[#5B52EB]/15", title: "Plataforma centralizada", desc: "Todo lo que necesitas en un único espacio inteligente." },
  { icon: Link2, color: "text-[#7AD7C4]", bg: "bg-[#7AD7C4]/10", title: "Conecta personas y cosas", desc: "Calendarios, tareas, proyectos, conversaciones y más." },
  { icon: Waves, color: "text-[#FF7D6E]", bg: "bg-[#FF7D6E]/10", title: "Menos ruido, más claridad", desc: "Interfaz calmada para que te enfoques en lo importante." },
  { icon: Sparkles, color: "text-[#FFB878]", bg: "bg-[#FFB878]/10", title: "Inteligencia que te anticipa", desc: "Sugerencias, automatizaciones y contexto en el momento justo." },
];

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.46a5.53 5.53 0 0 1-2.4 3.63v3h3.87c2.27-2.09 3.57-5.17 3.57-8.82Z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.08.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24Z" />
    <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.9 12c0-.79.14-1.56.37-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1Z" />
    <path fill="#EA4335" d="M12 4.76c1.76 0 3.35.6 4.6 1.8l3.44-3.44A11.98 11.98 0 0 0 1.29 6.62l3.98 3.1c.95-2.85 3.6-4.96 6.73-4.96Z" />
  </svg>
);

const inputCls = "h-11 bg-white/[0.05] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#766EFF]/60 focus-visible:ring-offset-0";

export default function AuthPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/";
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.googleError) {
      toast.error(location.state.googleError);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = mode === "login" ? { email, password } : { name, email, password };
      const { data } = await api.post(`/auth/${mode}`, payload);
      setUser(data);
      toast.success(mode === "login" ? `Hola de nuevo, ${data.name.split(" ")[0]}` : "Cuenta creada. ¡Bienvenido a Nuxxum!");
      navigate(redirect);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#171927] text-white overflow-x-hidden">
      <div className="pointer-events-none fixed inset-0 opacity-60"
        style={{ background: "radial-gradient(ellipse at 15% 10%, #5B52EB26 0%, transparent 45%), radial-gradient(ellipse at 85% 90%, #7AD7C414 0%, transparent 45%)" }} />

      <LaunchPromoBanner />

      <header className="relative max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center gap-3">
        <NuxxumLogo size={38} />
        <NuxxumWordmark className="text-2xl text-white" />
        <button
          data-testid="landing-lang-toggle"
          onClick={() => {
            const next = getEffectiveLang(null) === "en" ? "es" : "en";
            localStorage.setItem("nuxxum_lang", next);
            window.location.reload();
          }}
          className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-white/70 hover:text-white bg-white/10 hover:bg-white/15 rounded-full px-3.5 h-8 transition-colors"
          title={getEffectiveLang(null) === "en" ? "Cambiar a español" : "Switch to English"}
        >
          🌐 {getEffectiveLang(null) === "en" ? "ES · Español" : "EN · English"}
        </button>
      </header>

      <main className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="grid lg:grid-cols-[1.15fr,420px] gap-10 xl:gap-16 items-start">
          {/* Collage + tagline */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-4 gap-3">
              {COLLAGE.map((c, idx) => (
                <div key={c.label}
                  className={`relative rounded-2xl overflow-hidden h-[430px] ring-1 ring-white/10 ${idx % 2 === 1 ? "mt-10" : ""}`}>
                  <img src={c.img} alt={c.label} loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <span className="absolute bottom-3 left-3 text-xs font-semibold bg-black/45 backdrop-blur-md px-2.5 py-1 rounded-full">
                    {c.emoji} {c.label}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-8 space-y-2 animate-fade-up">
              <h1 className="font-heading text-3xl xl:text-4xl font-bold tracking-tight">
                Todo en su sitio. <span className="text-[#9A94FF]">Todos conectados.</span>
              </h1>
              <p className="text-white/60 text-lg">El núcleo donde todo conecta y encuentra su sitio.</p>
            </div>
          </div>

          {/* Login card */}
          <div className="w-full max-w-md mx-auto lg:mx-0 lg:mt-2">
            <div className="lg:hidden mb-8 space-y-1">
              <h1 className="font-heading text-3xl font-bold tracking-tight">Todo en su sitio. <span className="text-[#9A94FF]">Todos conectados.</span></h1>
              <p className="text-white/60">El núcleo donde todo conecta y encuentra su sitio.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-7 sm:p-8 space-y-6 shadow-2xl shadow-black/40 animate-fade-up"
              data-testid="auth-card">
              <div>
                <h2 className="font-heading text-2xl font-bold tracking-tight">
                  {mode === "login" ? "Inicia sesión" : "Crea tu cuenta"}
                </h2>
                <p className="text-white/50 mt-1 text-sm">
                  {mode === "login" ? "Accede a tus Espacios" : "Empieza a organizar tu mundo"}
                </p>
              </div>

              <form onSubmit={submit} className="space-y-4">
                {mode === "register" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-white/70 text-xs">Nombre completo</Label>
                    <Input id="name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Carlos García" required maxLength={100} className={inputCls} />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-white/70 text-xs">Correo electrónico</Label>
                  <Input id="email" data-testid="auth-email-input" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" required className={inputCls} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-white/70 text-xs">Contraseña</Label>
                  <Input id="password" data-testid="auth-password-input" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" required minLength={6} className={inputCls} />
                </div>
                <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer select-none w-fit">
                  <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="auth-remember-checkbox"
                    className="border-white/25 data-[state=checked]:bg-[#5B52EB] data-[state=checked]:border-[#5B52EB]" />
                  Recordarme
                </label>
                {mode === "login" && (
                  <Link
                    to="/forgot-password"
                    data-testid="auth-forgot-password-link"
                    className="block text-sm text-[#9A94FF] hover:underline w-fit"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                )}
                <Button type="submit" data-testid="auth-submit-btn" disabled={loading}
                  className="w-full h-11 text-sm font-semibold bg-gradient-to-r from-[#5B52EB] to-[#766EFF] hover:opacity-90 transition-opacity border-0 text-white">
                  {loading ? "Un momento..." : mode === "login" ? "Entrar" : "Crear cuenta"}
                </Button>
              </form>

              <p className="text-sm text-white/50 text-center">
                {mode === "login" ? "¿No tienes cuenta?" : "¿Ya tienes cuenta?"}{" "}
                <button data-testid="auth-toggle-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")}
                  className="text-[#9A94FF] font-semibold hover:underline">
                  {mode === "login" ? "Regístrate" : "Inicia sesión"}
                </button>
              </p>

              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-white/10" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-white/35">o continúa con</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>

              <button type="button" onClick={googleLogin} data-testid="auth-google-btn"
                className="w-full h-11 rounded-lg border border-white/15 bg-white/[0.06] hover:bg-white/[0.12] transition-colors flex items-center justify-center gap-2.5 text-sm font-medium">
                <GoogleIcon /> Continuar con Google
              </button>
            </div>
          </div>
        </div>

        {/* Pilares de marca */}
        <section className="mt-16 lg:mt-20 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="auth-value-props">
          {VALUE_PROPS.map((v, i) => (
            <div key={v.title}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-2.5 hover:border-white/20 hover:bg-white/[0.05] transition-colors animate-fade-up"
              style={{ animationDelay: `${i * 70}ms` }}>
              <div className={`h-9 w-9 rounded-lg ${v.bg} flex items-center justify-center`}>
                <v.icon className={`h-4.5 w-4.5 ${v.color}`} size={18} />
              </div>
              <p className={`text-sm font-semibold ${v.color}`}>{v.title}</p>
              <p className="text-xs text-white/50 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </section>
      </main>
    </div>
  );
}
